import { createFileRoute, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { ComponentDocsLink } from "@/components/catalog/card/component-docs-link";
import { ComponentPreviewView } from "@/components/catalog/card/component-preview-view";
import { ComponentCategoryPager } from "@/components/catalog/category/component-category-pager";
import {
  ComponentCategoryHeroIntro,
  ComponentCategorySeoContent,
} from "@/components/catalog/category/component-category-seo-content";
import { HideDuringSearch } from "@/components/catalog/sidebar/hide-during-search";
import { JsonLd } from "@/components/site/json-ld";
import { GridSkeleton } from "@/components/site/layout/grid-skeleton";
import { NotFoundContent } from "@/components/site/pages/not-found-page";
import { siteConfig } from "@/lib/config";
import {
  getCategoryInfo,
  getComponentsByCategory,
} from "@/lib/registry/core/registry";
import { getComponentCategorySeo } from "@/lib/registry/core/registry-seo-cache";
import { buildBreadcrumbJsonLd, buildPageMetadata } from "@/lib/seo/seo";
import { normalizeSlug } from "@/lib/utils";
import { categories } from "@/registry-audio/bases/categories";

export const Route = createFileRoute("/_site/components/$category")({
  beforeLoad: ({ params }) => {
    if (!getCategoryInfo(normalizeSlug(params.category))) {
      throw notFound();
    }
  },
  component: CategoryComponentsPage,
  notFoundComponent: NotFoundContent,
  head: ({ params }) => {
    const normalized = normalizeSlug(params.category);
    const categoryLabel = getCategoryInfo(normalized)?.label ?? params.category;
    const seo = getComponentCategorySeo(normalized);

    return buildPageMetadata({
      description: seo.description,
      keywords: [
        seo.title,
        `shadcn ${categoryLabel.toLowerCase()}`,
        `shadcn ${categoryLabel.toLowerCase()} components`,
        `${categoryLabel} React examples`,
        "open source shadcn components",
        ...seo.keywords,
      ],
      path: `/components/${normalized}`,
      title: seo.title,
      titleSuffix: siteConfig.metadata.titleSuffixes.componentCategory,
    });
  },
});

function CategoryComponentsPage() {
  const normalized = normalizeSlug(Route.useParams().category);
  const seo = getComponentCategorySeo(normalized);
  const catalogItems = getComponentsByCategory(normalized);
  const faqJsonLd = seo.content?.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: seo.content.faqs.map((faq) => ({
          "@type": "Question",
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
          name: faq.question,
        })),
      }
    : null;
  const header = (
    <section>
      <div className="w-full px-6 pt-8 pb-6 sm:px-8 xl:px-10">
        <div className="flex w-full flex-wrap items-end justify-between gap-3">
          <h1 className="mt-3 min-w-0 flex-1 font-bold text-balanc text-xl sm:text-3xl">
            {seo.title}
          </h1>
          <ComponentDocsLink
            slug={
              categories.find((item) => item.name === normalized)?.docs ??
              normalized
            }
          />
        </div>
        {seo.intro ? <ComponentCategoryHeroIntro intro={seo.intro} /> : null}
      </div>
    </section>
  );
  const footer = (
    <>
      <ComponentCategorySeoContent seo={seo} />
      <ComponentCategoryPager currentCategory={normalized} />
    </>
  );
  return (
    <>
      <JsonLd
        data={buildBreadcrumbJsonLd([
          { name: siteConfig.name, path: "/" },
          { name: "Components", path: "/components" },
          { name: seo.title, path: `/components/${normalized}` },
        ])}
      />
      {faqJsonLd ? <JsonLd data={faqJsonLd} /> : null}
      <Suspense fallback={header}>
        <HideDuringSearch results>{header}</HideDuringSearch>
      </Suspense>
      <Suspense fallback={<GridSkeleton count={4} />}>
        <ComponentPreviewView catalogItems={catalogItems} />
      </Suspense>
      <Suspense fallback={footer}>
        <HideDuringSearch>{footer}</HideDuringSearch>
      </Suspense>
    </>
  );
}
